import { useCallback, useState } from 'react';
import { Button, colors, Tooltip } from '@sweep-io/sweep-design';
import { ButtonVariants } from '@sweep-io/sweep-design/dist/components/Button/types';
import useAgentsChats from '../useAgentsChats';
import { Info } from '@sweep-io/sweep-design/dist/icons';
import { Box } from '@mui/material';

interface UsePromptInChatButtonProps {
  promptText: string;
  variant?: ButtonVariants;
  size?: 'small' | 'medium';
  disabled?: boolean;
  onSuccess?: () => void;
}

export const UsePromptInChatButton = ({
  promptText,
  variant = 'flat-grey',
  size,
  disabled,
  onSuccess,
}: UsePromptInChatButtonProps) => {
  const { onConfirmMessage, activeAgent } = useAgentsChats();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = useCallback(async () => {
    if (!activeAgent || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onConfirmMessage({
        messageStr: promptText,
        agentId: activeAgent.id,
        agentType: activeAgent.type,
        isFirstMessage: true,
      });
      onSuccess?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [activeAgent, isSubmitting, onConfirmMessage, promptText, onSuccess]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        variant={variant}
        size={size}
        startIconName="ArrowUp"
        onClick={handleClick}
        loading={isSubmitting}
        disabled={disabled}
      >
        Use this Prompt in Chat
      </Button>
      <Tooltip title="Applies the prompt in a new conversation" placement="bottom">
        <Info size="medium" color={disabled ? colors.grey[500] : colors.grey[900]} />
      </Tooltip>
    </Box>
  );
};
